import { useState } from "react";

import { Empty, Failure, Panel } from "../components/Panel.js";
import { useResource } from "../hooks.js";
import { apiPost } from "../lib/api.js";
import { type ApiKeyRecord } from "../types.js";

export function KeysPage({ organization }: { organization: string }) {
  const path = `/app/studio/organization/${organization}/apikey`;
  const keys = useResource<ApiKeyRecord[]>(path);
  const [name, setName] = useState("");
  const [minted, setMinted] = useState<string>();
  const [failure, setFailure] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function act(work: () => Promise<unknown>) {
    setBusy(true);
    setFailure(undefined);
    try {
      await work();
      keys.reload();
    } catch (error) {
      setFailure((error as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Panel title="Create a key">
        <form
          className="flex flex-wrap gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!name.trim()) return;
            act(async () => {
              const result = await apiPost<{ key: string }>(path, {
                name: name.trim(),
              });
              setMinted(result.key);
              setName("");
            });
          }}
        >
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="What is this key for?"
            className="flex-1 rounded border border-black/15 px-3 py-2"
          />
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="rounded bg-jay px-4 py-2 font-medium text-white hover:opacity-90 disabled:opacity-40"
          >
            Create
          </button>
        </form>
        {minted ? (
          <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3">
            <p className="mb-2 text-sm font-medium text-amber-900">
              Copy this key now. It is never shown again.
            </p>
            <code className="block break-all rounded bg-white px-3 py-2 font-mono text-sm">
              {minted}
            </code>
            <button
              type="button"
              onClick={() => setMinted(undefined)}
              className="mt-2 text-sm text-amber-900 underline"
            >
              I have copied it
            </button>
          </div>
        ) : null}
        {failure ? (
          <div className="mt-4">{<Failure>{failure}</Failure>}</div>
        ) : null}
      </Panel>

      <Panel title="Keys">
        {keys.loading ? <Empty>Loading</Empty> : null}
        {keys.error ? <Failure>{keys.error.message}</Failure> : null}
        {keys.value && keys.value.length === 0 ? (
          <Empty>No keys yet</Empty>
        ) : null}
        {keys.value && keys.value.length > 0 ? (
          <table className="w-full text-left text-sm">
            <thead className="text-ink/50">
              <tr>
                <th className="pb-2 font-medium">Name</th>
                <th className="pb-2 font-medium">Ends in</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 font-medium">Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.value.map((key) => (
                <tr key={key.id} className="border-t border-black/5">
                  <td className="py-2">{key.name}</td>
                  <td className="py-2 font-mono">{key.label}</td>
                  <td className="py-2">{key.status}</td>
                  <td className="py-2 text-ink/60">
                    {key.lastUsedAt
                      ? new Date(key.lastUsedAt).toLocaleString()
                      : "never"}
                  </td>
                  <td className="py-2 text-right">
                    {key.status === "active" ? (
                      <>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            act(async () => {
                              const result = await apiPost<{ key: string }>(
                                `${path}/${key.id}/rotate`,
                                { mode: "graceful" },
                              );
                              setMinted(result.key);
                            })
                          }
                          className="mr-3 text-jay hover:underline"
                        >
                          Rotate
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            act(() => apiPost(`${path}/${key.id}/revoke`))
                          }
                          className="text-red-700 hover:underline"
                        >
                          Revoke
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Panel>
    </>
  );
}
